import React from 'react';
import { Loader2 } from 'lucide-react';
import { YearlyBreakdownRow } from '@/types/savingsCalculator';

interface YearlyBreakdownTableProps {
  rows: YearlyBreakdownRow[];
  loading?: boolean;
}

const fmt = (amount: number) =>
  amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const YearlyBreakdownTable: React.FC<YearlyBreakdownTableProps> = ({ rows, loading = false }) => {
  // Totals row
  const totals = rows.reduce(
    (acc, r) => ({
      oneTimeFees: acc.oneTimeFees + r.oneTimeFees,
      oneTimeCount: acc.oneTimeCount + r.oneTimeCount,
      subscriptionFees: acc.subscriptionFees + r.subscriptionFees,
      subscriptionCount: acc.subscriptionCount + r.subscriptionCount,
      totalFees: acc.totalFees + r.totalFees,
      totalCount: acc.totalCount + r.totalCount,
    }),
    { oneTimeFees: 0, oneTimeCount: 0, subscriptionFees: 0, subscriptionCount: 0, totalFees: 0, totalCount: 0 },
  );

  return (
    <div className="border-2 border-muted rounded-xl bg-white dark:bg-background p-6">
      <h3 className="text-base text-muted-foreground mb-4">Yearly Breakdown</h3>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No transaction data available</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-3 font-medium">Year</th>
                <th className="text-center py-3 font-medium">One-Time Purchases</th>
                <th className="text-center py-3 font-medium">Subscriptions</th>
                <th className="text-right py-3 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.year} className="border-b border-muted/50">
                  <td className="py-3 font-semibold text-foreground">{row.year}</td>
                  <td className="py-3 text-center">
                    <span className="text-red-500 dark:text-red-400 font-semibold">
                      ${fmt(row.oneTimeFees)}
                    </span>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      {row.oneTimeCount.toLocaleString()} transaction{row.oneTimeCount !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span className="text-red-500 dark:text-red-400 font-semibold">
                      ${fmt(row.subscriptionFees)}
                    </span>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      {row.subscriptionCount.toLocaleString()} transaction{row.subscriptionCount !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <span className="text-red-500 dark:text-red-400 font-semibold">
                      ${fmt(row.totalFees)}
                    </span>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      {row.totalCount.toLocaleString()} transaction{row.totalCount !== 1 ? 's' : ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="py-3 font-bold text-foreground">Total</td>
                <td className="py-3 text-center">
                  <span className="text-red-500 dark:text-red-400 font-bold">
                    ${fmt(totals.oneTimeFees)}
                  </span>
                  <br />
                  <span className="text-xs text-muted-foreground">
                    {totals.oneTimeCount.toLocaleString()} transaction{totals.oneTimeCount !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="py-3 text-center">
                  <span className="text-red-500 dark:text-red-400 font-bold">
                    ${fmt(totals.subscriptionFees)}
                  </span>
                  <br />
                  <span className="text-xs text-muted-foreground">
                    {totals.subscriptionCount.toLocaleString()} transaction{totals.subscriptionCount !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <span className="text-red-500 dark:text-red-400 font-bold">
                    ${fmt(totals.totalFees)}
                  </span>
                  <br />
                  <span className="text-xs text-muted-foreground">
                    {totals.totalCount.toLocaleString()} transaction{totals.totalCount !== 1 ? 's' : ''}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default YearlyBreakdownTable;
